import { BuilderComponent } from '@builder.io/react';
import { useRouter } from 'next/router';
import { FC, useContext, useEffect } from 'react';

import ArticleAuthorInfo from '@/components/article-author-info/article-author-info';
import ArticleHero from '@/components/article-hero/article-hero';
import DijiBlogHighlightArticle from '@/components/diji-blog-highlight-article/diji-blog-highlight-article';
import DijiBlogRelatedArticles from '@/components/diji-blog-related-articles/diji-blog-related-articles';
import DefaultLayout from '@/components/layout';
import Loading from '@/components/loading';
import MetaBlock from '@/components/meta-block/MetaBlock';
import ShareIcons from '@/components/share-icons/share-icons';
import ArticleModel from '@/models/ArticleModel';
import { NavigationContext } from '@/shared/NavigationContext';
import { Article } from '@/types/article';
import { BUILDER_MODEL } from '@/types/builder.d';
import { getBlockDataMap } from '@/utils/Builder';

import styles from './article.module.scss';

interface ArticlePageProps {
  upNextArticle: Article | null;
  relatedArticles: [];
  page: any;
}

const ArticlePage: FC<ArticlePageProps> = ({ upNextArticle, relatedArticles, page }) => {
  const article = ArticleModel.fromBuilderContent(page);
  const router = useRouter();
  const { setTheme } = useContext(NavigationContext);

  let targetUrl = article?.url;
  if (typeof window !== 'undefined') {
    targetUrl = `${window?.origin || ''}${article?.url}`;
  }

  useEffect(() => {
    setTheme('dark');

    return () => {
      setTheme('light');
    };
  }, []);

  if (router.isFallback || !article) {
    return <Loading />;
  }

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Article',
    headline: article.title,
    image: article.leadImage || article.leadPortraitImage || '',
    author: [
      {
        '@type': 'Person',
        name: article.author?.name,
        jobTitle: article.author?.jobTitle,
      },
      {
        '@type': 'Organization',
        name: 'Dijitally',
        url: 'https://www.dijitally.com/',
      },
    ],
    datePublished: article.firstPublished ? new Date(article.firstPublished).toJSON() : null,
    dateModified: article.lastUpdated ? new Date(article.lastUpdated).toJSON() : null,
    publisher: {
      '@type': 'Organization',
      name: 'Dijitally',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.dijitally.com/images/logo.png',
      },
    },
  };

  return (
    <>
      <MetaBlock
        title={article.seoTitle ?? article.title}
        description={article.seoDescription}
        leadImage={article.leadImage}
        keywords={article.keywords}
      >
        <script type="application/ld+json">{JSON.stringify(schema, null, 2)}</script>
      </MetaBlock>
      <DefaultLayout>
        <ArticleHero article={article} />

        <BuilderComponent
          model={BUILDER_MODEL.ARTICLE}
          content={page}
          data={getBlockDataMap(page)}
        />
        <div className={styles.footer}>
          <div className={styles.divider} />
          <ArticleAuthorInfo articleAuthor={article.author} />
          <ShareIcons shareUrl={targetUrl} />
          <DijiBlogHighlightArticle fixedArticle={upNextArticle} arrowText="Up Next" />
          <DijiBlogRelatedArticles articles={relatedArticles} />
        </div>
      </DefaultLayout>
    </>
  );
};

export default ArticlePage;
